import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown as UnfoldMoreIcon,
  X,
  Trash2,
  Loader,
  Pencil,
  Check,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { RootState } from '../../reducers';
import { Member } from '../../types';
import { pushModal } from '../globalModals/globalModalsSlice';
import { useTeamTags } from '../../hooks/useTags';
import {
  removeTeamMemberThunk,
  selectTeam,
  uploadTeamPictureThunk,
  deleteTeamPictureThunk,
  renameTeamThunk,
} from '../team/teamSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  SettingsSection,
  SettingsLayout,
} from '../../components/ui/settings-section';
import { getTeamLogoUrl, isCustomTeamLogo } from '../../lib/teamLogoUtils';

const TeamSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedTeam, teams } = useSelector((state: RootState) => ({
    selectedTeam: state.team?.selectedTeam,
    teams: state.team?.teams,
  }));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const teamSpaceMembers = selectedTeam?.teamSpaceMembers || [];
  const { getTeamTag } = useTeamTags();

  // Get the logo to display with proper fallback chain
  const displayLogo = getTeamLogoUrl(
    selectedTeam?.pictureUrl,
    getTeamTag('team_logo_url'),
  );

  // Check if current logo is custom (uploaded via pictureUrl)
  const hasCustomLogo = isCustomTeamLogo(selectedTeam?.pictureUrl);

  const handleOpenModal = () => {
    dispatch(pushModal({ type: 'invite-by-link-modal' }));
  };

  const handleRemove = (memberId: string) => {
    if (selectedTeam) {
      const { teamId } = selectedTeam;
      dispatch(removeTeamMemberThunk({ teamId, userId: memberId }));
    }
  };

  const getStatus = (member: Member) => {
    const isAccepted = member.inviteAccepted || !!member.inviteAcceptedAt;

    if (!isAccepted && member.unregistered) {
      return t('team.memberStatus.unregistered');
    }
    if (!isAccepted) {
      return t('team.memberStatus.pending');
    }
    if (member.status !== false) {
      return t('team.memberStatus.active');
    }
    return t('team.memberStatus.disabled');
  };

  if (!selectedTeam) {
    return null;
  }

  const handleTeamSelect = (teamId: string) => {
    const newSelectedTeam = teams.find((team) => team.teamId === teamId);
    if (!newSelectedTeam) return;
    dispatch(selectTeam(newSelectedTeam));
  };

  const showToast = (props: {
    description: string;
    variant?: 'default' | 'destructive';
  }) => {
    if (props.variant === 'destructive') {
      toast.error(props.description);
    } else {
      toast(props.description);
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTeam) return;

    setIsUploading(true);
    try {
      await dispatch(
        uploadTeamPictureThunk({
          teamId: selectedTeam.teamId,
          file,
          toast: showToast,
        }),
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!selectedTeam) return;

    setIsUploading(true);
    try {
      // Delete the team picture (clears pictureUrl, falls back to team_logo_url)
      await dispatch(
        deleteTeamPictureThunk({
          teamId: selectedTeam.teamId,
          toast: showToast,
        }),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleStartEditName = () => {
    if (selectedTeam) {
      setEditName(selectedTeam.name);
      setIsEditingName(true);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditName('');
  };

  const handleSaveRename = async () => {
    if (!selectedTeam || !editName.trim() || editName === selectedTeam.name) {
      handleCancelEditName();
      return;
    }

    setIsRenaming(true);
    try {
      await dispatch(
        renameTeamThunk({
          teamId: selectedTeam.teamId,
          newName: editName.trim(),
          toast: showToast,
        }),
      );
      setIsEditingName(false);
      setEditName('');
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <SettingsLayout>
      {/* Current Team - Combined selection and rename */}
      <SettingsSection
        label={t('team.currentTeam')}
        description={t('team.selectWorkspace')}
      >
        <div className="group flex items-center h-9">
          {!isEditingName && (
            <Pencil className="h-3 w-3 mr-1.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          )}
          {isEditingName ? (
            <div className="flex items-center">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-7 w-auto min-w-[120px] px-2 py-0 text-sm"
                disabled={isRenaming}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveRename();
                  if (e.key === 'Escape') handleCancelEditName();
                }}
                onBlur={handleSaveRename}
                autoFocus
              />
              {isRenaming && (
                <Loader className="h-3 w-3 ml-2 animate-spin text-muted-foreground" />
              )}
            </div>
          ) : (
            <div className="flex items-center">
              <button
                type="button"
                onClick={handleStartEditName}
                className="text-sm text-left hover:text-foreground/80 transition-colors"
              >
                {selectedTeam.name}
              </button>
              {teams.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 ml-1"
                    >
                      <UnfoldMoreIcon className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="min-w-[140px] max-w-[200px]"
                  >
                    {teams.map(({ name, teamId }) => {
                      const isSelected = selectedTeam?.teamId === teamId;
                      return (
                        <DropdownMenuItem
                          key={teamId}
                          onClick={() => handleTeamSelect(teamId)}
                        >
                          <span className="flex-1 truncate" title={name}>
                            {name}
                          </span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-primary ml-2" />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Team Logo */}
      <SettingsSection
        label={t('team.teamLogo')}
        description={t('team.logoDescription')}
      >
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileSelect}
          />

          {/* Always show a logo - either custom or fallback */}
          <div className="relative group">
            <img
              src={displayLogo}
              alt={t('team.teamLogoAlt')}
              className="w-10 h-10 object-contain rounded border"
            />
            {/* Only show remove button for custom uploaded logos */}
            {hasCustomLogo && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                disabled={isUploading}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                title={t('team.removeLogo') || 'Remove logo'}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {isUploading ? (
            <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Button
              variant="minimal"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('team.uploadLogo')}
            </Button>
          )}
        </div>
      </SettingsSection>

      {/* User Management */}
      <div className="space-y-4">
        <SettingsSection
          label={t('team.userManagement')}
          description="Manage team members and their access"
          showSeparator={false}
        >
          <Button variant="minimal" onClick={handleOpenModal}>
            {t('team.addUsers')}
          </Button>
        </SettingsSection>

        {teamSpaceMembers.length > 0 && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium">Member</TableHead>
                  <TableHead className="font-medium">Role</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamSpaceMembers.map((member) => (
                  <TableRow key={member._id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{member.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {member.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{member.role}</span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-secondary text-secondary-foreground">
                        {getStatus(member)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemove(member._id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove member</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </SettingsLayout>
  );
};

export default TeamSettings;
